/* ============================================================
   THE ARTISAN NOOK - homepage interactions
   hero ritual scrub · worlds hover · parallax · seasonal tabs
   (site.js has already created Lenis + injected chrome)
   ============================================================ */
(function () {
  "use strict";

  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  // Treat any touch device as "narrow" too: a phone in Chrome's "Desktop site" mode reports
  // ~980px, which used to hand it the desktop horizontal Worlds slider (unusable on a phone,
  // and its pin left a large blank gap). Real desktops report hover+fine pointer -> unaffected.
  const coarsePointer = matchMedia("(hover: none) and (pointer: coarse)").matches;
  const narrow = matchMedia("(max-width: 860px)").matches || coarsePointer;
  const hasGsap = typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  // scrollHero = desktop-only heavy effects (particle canvas + cursor glow). UNCHANGED.
  const scrollHero = !reduced && !narrow && hasGsap;
  // heroScrub = the scroll-scrub mechanic itself - now on BOTH desktop and mobile.
  const heroScrub = !reduced && hasGsap;
  const $ = (id) => document.getElementById(id);

  /* ====================================================
     HERO RITUAL
     ==================================================== */
  const video = $("hero-video");
  let vReady = false, vDur = 0, vStart = 0;
  /* A phone's decoder services a seek in ~25-50ms, but scroll fires ~60x a second. Assigning
     currentTime on every tick therefore queues far more seeks than the decoder can retire, and
     the backlog shows up as the video jumping/stalling in patches. On mobile we coalesce: keep
     only the LATEST target and hand it over when the decoder reports idle, so every seek that is
     issued actually lands. Desktop keeps the original direct assignment (seeks there are ~8-12ms
     and already land within a frame) - its behaviour is unchanged. */
  let vPending = null, vBusy = false, vBusyAt = 0;
  function vApply() {
    if (vBusy && performance.now() - vBusyAt < 400) return;   // decoding; 400ms = stall guard
    if (vPending === null) return;
    const t = vPending; vPending = null;
    if (Math.abs(video.currentTime - t) < 0.012) return;
    vBusy = true; vBusyAt = performance.now();
    video.currentTime = t;
  }
  function vSeek(p) {
    if (!vReady || !vDur) return;
    const t = Math.min(vDur - 0.05, Math.max(vStart, vStart + p * (vDur - vStart - 0.05)));
    if (!narrow) {
      if (Math.abs(video.currentTime - t) > 0.02) video.currentTime = t;
      return;
    }
    vPending = t; vApply();
  }
  if (video) {
    video.muted = true;
    const d = video.dataset || {};
    const mobileSrc = d.mobileSrc || null;          // 1.7MB playback loop (fallback)
    const mobileScrubSrc = d.mobileScrubSrc || null; // 9:16 short-GOP scrub file
    const scrubSrc = d.scrubSrc || null;             // desktop 16:9 scrub file
    // The film is fetched ONCE as a blob so every seek is served from memory - that's what
    // makes the scrub land on every frame instead of waiting on the network. Desktop uses the
    // 16:9 cut; mobile uses the small 9:16 cut (right framing + far less data).
    const blobTarget = narrow ? mobileScrubSrc : scrubSrc;
    const willPrefetch = heroScrub && blobTarget && window.fetch;
    if (narrow) {
      // don't let the element download the desktop <source>; the blob supplies the real src,
      // the poster (frame 0) covers the gap.
      if (d.mobilePoster) video.poster = d.mobilePoster;
      video.preload = "none";
    } else {
      video.preload = willPrefetch ? "metadata" : (scrollHero ? "auto" : "metadata");
    }
    // mobile only: the decoder tells us when it has finished a seek, which drives vApply()
    if (narrow) {
      video.addEventListener("seeked", () => { vBusy = false; vApply(); });
      video.addEventListener("error", () => { vBusy = false; });
    }
    const ready = () => { vDur = video.duration || 0; vReady = true; if (heroScrub) { video.pause(); vSeek(0); } else if (!narrow) { video.pause(); vSeek(0.985); } /* mobile no-gsap: let video loop freely */ };
    video.addEventListener("loadedmetadata", ready); // not once: re-fires when the scrub cut swaps in
    if (!narrow) { if (video.readyState >= 1) ready(); else video.load(); }
    if (willPrefetch) {
      fetch(blobTarget)
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.blob(); })
        .then((b) => { vReady = false; video.src = URL.createObjectURL(b); video.load(); })
        .catch(() => {
          // fall back to streaming the real file directly (still scrubs, just seeks slower)
          vReady = false; video.src = narrow ? (mobileSrc || mobileScrubSrc) : scrubSrc;
          video.preload = "auto"; video.load();
        });
    } else if (narrow && (mobileSrc || mobileScrubSrc)) {
      video.src = mobileSrc || mobileScrubSrc; video.load(); // reduced-motion mobile: plain file
    }
  }

  /* particles */
  const particles = (function () {
    const canvas = $("hero-particles");
    if (!canvas || !scrollHero) return null;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(devicePixelRatio || 1, 2);
    let w, h, list, intensity = 0.25;
    const resize = () => { w = innerWidth; h = innerHeight; canvas.width = w * dpr; canvas.height = h * dpr; canvas.style.width = w + "px"; canvas.style.height = h + "px"; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    const make = () => ({ x: Math.random() * (w || innerWidth), y: (h || innerHeight) + Math.random() * 300, vx: (Math.random() - 0.5) * 0.3, vy: -0.3 - Math.random() * 0.9, size: 0.6 + Math.random() * 2.2, a: 0.1 + Math.random() * 0.5, ember: Math.random() < 0.4, ph: Math.random() * 6.28 });
    resize(); list = Array.from({ length: 70 }, make);
    (function draw() {
      ctx.clearRect(0, 0, w, h);
      const n = Math.floor(list.length * intensity);
      for (let i = 0; i < n; i++) { const p = list[i]; p.ph += 0.01; p.x += p.vx + Math.sin(p.ph) * 0.25; p.y += p.vy * (0.6 + intensity); if (p.y < -10) { Object.assign(p, make()); p.y = h + 10; } const wm = p.ember; const fl = wm ? 0.7 + Math.sin(p.ph * 3) * 0.3 : 1; ctx.beginPath(); ctx.fillStyle = `rgba(${wm ? 244 : 226},${wm ? 199 : 196},${wm ? 123 : 168},${p.a * intensity * fl})`; ctx.arc(p.x, p.y, p.size, 0, 6.2832); ctx.fill(); }
      requestAnimationFrame(draw);
    })();
    addEventListener("resize", resize);
    return { set: (v) => (intensity = Math.max(0, Math.min(1, v))) };
  })();

  /* cursor-reactive glow + film lean */
  const glow = $("hero-glow");
  if (scrollHero && glow) {
    let mx = 0.5, my = 0.55, gx = 0.5, gy = 0.55;
    addEventListener("mousemove", (e) => { mx = e.clientX / innerWidth; my = e.clientY / innerHeight; });
    // Animate with the `translate` property (compositor-only, and it does NOT clash with the
    // `transform` GSAP writes for the glow's scale). The old version wrote marginLeft/marginTop
    // with calc() every single frame, which forced a full layout on each one - that was a
    // constant drag on the scroll scrub. Also: skip writes once the cursor settles.
    let px = null, py = null;
    (function tick() {
      gx += (mx - gx) * 0.08; gy += (my - gy) * 0.08;
      if (px === null || Math.abs(gx - px) > 0.0004 || Math.abs(gy - py) > 0.0004) {
        px = gx; py = gy;
        const dx = (gx - 0.5) * 18, dy = (gy - 0.5) * 18;
        glow.style.translate = `${dx}vw ${dy}vh`;
        if (video) video.style.translate = `${-dx * 0.12}px ${-dy * 0.12}px`;
      }
      requestAnimationFrame(tick);
    })();
  }

  /* caption beats */
  const beats = [
    { at: 0,    t: "Warmth in every breath" },
    { at: 0.22, t: "Rose, amber, sandalwood" },
    { at: 0.40, t: "Then - fire" },
    { at: 0.68, t: "Love in the Air" },
  ];
  let last = -1;
  const capText = $("caption-text"), cap = $("hero-caption");
  function caption(p) {
    if (!cap) return;
    if (p >= 0.85) { cap.style.opacity = 0; return; }
    let idx = 0; for (let i = 0; i < beats.length; i++) if (p >= beats[i].at) idx = i;
    cap.style.opacity = 1;
    if (idx !== last) { last = idx; capText.style.opacity = 0; capText.style.transform = "translateY(8px)";
      setTimeout(() => { capText.textContent = beats[idx].t; capText.style.opacity = 1; capText.style.transform = "none"; }, 170); }
  }

  function heroStatic() {
    document.body.dataset.heroMode = "static";
    const n = $("header-note"); if (n) n.textContent = "Handcrafted rituals";
    if (narrow) {
      // mobile: play the looping video. autoplay re-added here for MOBILE ONLY
      // (desktop never enters this branch, so the scroll-scrub stays intact).
      if (video) {
        video.loop = true; video.muted = true; video.defaultMuted = true; video.preload = "auto";
        video.setAttribute("playsinline", ""); video.setAttribute("webkit-playsinline", "");
        video.setAttribute("muted", ""); video.setAttribute("autoplay", "");
        var tryPlay = function () { try { var p = video.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} };
        tryPlay();
        // iOS may block autoplay until the first interaction - retry on touch/scroll
        var kick = function () { tryPlay(); window.removeEventListener("touchstart", kick); window.removeEventListener("scroll", kick); };
        window.addEventListener("touchstart", kick, { passive: true });
        window.addEventListener("scroll", kick, { passive: true });
      }
    } else {
      vSeek(0.985); // desktop fallback (no GSAP): seek to a lit frame
    }
  }

  function heroScroll() {
    document.body.dataset.heroMode = "scroll";
    gsap.registerPlugin(ScrollTrigger);
    if (narrow) {
      // Touch scroll (no Lenis on mobile) delivers scroll async during momentum, which makes a
      // pinned video-scrub stutter. normalizeScroll hands scrolling to GSAP so the scrub tracks
      // the finger; ignoreMobileResize stops the iOS URL-bar show/hide from jumping the pin.
      try { ScrollTrigger.config({ ignoreMobileResize: true }); } catch (e) {}
      try { ScrollTrigger.normalizeScroll(true); } catch (e) {}
      // iOS won't paint currentTime seeks on a muted inline <video> until it's been "activated";
      // a muted play()->pause() primes it. Retry on first touch in case autoplay was blocked.
      var primeVid = function () { if (!video) return; try { var pr = video.play(); if (pr && pr.then) pr.then(function () { video.pause(); }).catch(function () {}); else video.pause(); } catch (e) {} };
      primeVid();
      window.addEventListener("touchstart", function onceTouch() { primeVid(); window.removeEventListener("touchstart", onceTouch); }, { passive: true });
    }
    const flash = $("hero-flash"), vig = $("hero-vignette"), grade = $("hero-grade");
    const eyebrow = $("hero-eyebrow"), sub = $("hero-sub"), ctas = $("hero-ctas"), cue = $("hero-cue"), prog = $("hero-progress");
    const lines = document.querySelectorAll(".hero-line-inner");
    gsap.set(glow, { opacity: 0, scale: 0.5 }); gsap.set(flash, { opacity: 0 });
    // eyebrow / sub are optional (removed when their setting is blank) - keep everything null-safe
    gsap.set([eyebrow, sub, ctas].filter(Boolean), { opacity: 0, y: 14 }); gsap.set(lines, { yPercent: 110 });
    const setVig = gsap.quickSetter(vig, "opacity"), setGrade = gsap.quickSetter(grade, "opacity");

    ScrollTrigger.create({
      trigger: "#hero-home", start: "top top", end: narrow ? "+=260%" : "+=420%", scrub: narrow ? 0.5 : 0.7,
      pin: "#hero-stage", anticipatePin: 1, invalidateOnRefresh: true,
      onUpdate: (self) => {
        const p = self.progress;
        vSeek(p); if (prog) prog.textContent = Math.round(p * 100) + "%"; caption(p);
        if (cue) cue.style.opacity = p > 0.05 ? 0 : 0.8;
        if (particles) { if (p < 0.42) particles.set(0.25 + p * 0.4); else if (p < 0.66) particles.set(0.7 + (p - 0.42) * 1.2); else particles.set(Math.max(0.45, 1 - (p - 0.66) * 0.6)); }
        const fd = Math.abs(p - 0.5); flash.style.opacity = fd < 0.04 ? ((0.04 - fd) / 0.04) * 0.85 : 0;
        setGrade(p < 0.6 ? 0.9 : Math.max(0.45, 0.9 - (p - 0.6) * 1.1));
        setVig(p < 0.6 ? 1 : Math.max(0.42, 1 - (p - 0.6) * 1.4));
        if (p < 0.6) gsap.set(glow, { opacity: 0, scale: 0.5 });
        else { const g = Math.min(1, (p - 0.6) / 0.28); gsap.set(glow, { opacity: 0.25 + g * 0.75, scale: 0.6 + g * 0.6 }); }
        if (eyebrow) { eyebrow.style.opacity = p > 0.7 ? 1 : 0; eyebrow.style.transform = `translateY(${p > 0.7 ? 0 : 14}px)`; }
        lines.forEach((l, i) => { const k = Math.min(1, Math.max(0, (p - (0.74 + i * 0.05)) / 0.08)); l.style.transform = `translateY(${(1 - k) * 110}%)`; });
        if (sub) { sub.style.opacity = p > 0.86 ? 1 : 0; sub.style.transform = `translateY(${p > 0.86 ? 0 : 16}px)`; }
        const ctasOn = p > 0.9;
        ctas.style.opacity = ctasOn ? 1 : 0; ctas.style.transform = `translateY(${ctasOn ? 0 : 16}px)`;
        ctas.style.visibility = ctasOn ? "visible" : "hidden"; // invisible CTAs leave the tab order
        if (p > 0.92 && window.__runInView) window.__runInView();
      },
      onLeave: () => { if (window.__runInView) window.__runInView(); },
    });
    addEventListener("load", () => ScrollTrigger.refresh());
    // Desktop: refresh on resize. Mobile: DON'T refresh on the URL-bar resize (that causes the
    // pin jump ignoreMobileResize is preventing) - only re-measure on a real orientation change.
    if (narrow) addEventListener("orientationchange", () => setTimeout(() => ScrollTrigger.refresh(), 250));
    else addEventListener("resize", () => ScrollTrigger.refresh());
  }

  /* ====================================================
     FRAGRANCE WORLDS - hover to play (wired immediately)
     ==================================================== */
  document.querySelectorAll(".world-panel").forEach((w) => {
    const v = w.querySelector("video");
    if (!v) return;
    w.addEventListener("mouseenter", () => { w.classList.add("play"); v.play().catch(() => {}); });
    w.addEventListener("mouseleave", () => { w.classList.remove("play"); v.pause(); });
  });

  /* Touch devices have no hover, so the world films never played there. Play the panel that is
     actually on screen instead, and pause the others - only ever one decoding at a time, and the
     films only download once they're needed (they ship with preload="none"). */
  if (narrow && "IntersectionObserver" in window) {
    const panels = [...document.querySelectorAll(".world-panel")].filter((w) => w.querySelector("video"));
    if (panels.length) {
      const attempt = (v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
      /* The films ship with preload="none", so the FIRST play() call lands before any data exists
         and fails. Upgrade preload, kick a load(), and (re)play once the browser says it can - and
         again on the first touch, in case the autoplay policy refused the un-gestured attempt. */
      const ensurePlay = (w, v) => {
        v.muted = true; v.defaultMuted = true;
        v.setAttribute("muted", ""); v.setAttribute("playsinline", ""); v.setAttribute("webkit-playsinline", "");
        if (v.preload !== "auto") v.preload = "auto";
        if (v.readyState < 2) {
          v.addEventListener("canplay", function onCanPlay() {
            v.removeEventListener("canplay", onCanPlay);
            if (w.classList.contains("play")) attempt(v);
          });
          try { v.load(); } catch (err) {}
        }
        attempt(v);
      };
      let visible = null;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const w = e.target, v = w.querySelector("video");
          if (!v) return;
          if (e.isIntersecting) {
            visible = { w: w, v: v };
            w.classList.add("play");
            ensurePlay(w, v);
          } else {
            w.classList.remove("play");
            try { v.pause(); } catch (err) {}
          }
        });
      }, { threshold: 0.55 });   // "mostly filling the screen" = play
      panels.forEach((w) => io.observe(w));
      window.addEventListener("touchstart", function kickWorlds() {
        window.removeEventListener("touchstart", kickWorlds);
        if (visible && visible.w.classList.contains("play")) ensurePlay(visible.w, visible.v);
      }, { passive: true });
    }
  }

  // Worlds + craft ScrollTriggers must be created AFTER the hero pin spacer is added,
  // otherwise GSAP pin-compensation fires the worlds section too early (during the hero scroll).
  function startScrollAnimations() {
    if (!hasGsap || reduced || narrow) return;
    gsap.registerPlugin(ScrollTrigger);

    const track = document.getElementById("worldsTrack");
    const section = document.getElementById("worlds");
    if (track && section) {
      const railFill = document.getElementById("worldsRailFill");
      const countEl = document.getElementById("worldsCount");
      const scrollLen = () => Math.max(0, track.scrollWidth - innerWidth);
      gsap.to(track, {
        x: () => -scrollLen(),
        ease: "none",
        scrollTrigger: {
          trigger: section,
          pin: true,
          scrub: 0.8,
          start: "top top",
          end: () => "+=" + scrollLen(),
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            if (railFill) railFill.style.width = (self.progress * 100).toFixed(1) + "%";
            if (countEl) { const n = Math.min(5, Math.max(1, Math.round(self.progress * 4) + 1)); countEl.textContent = "0" + n + " / 05"; }
          },
        },
      });
      addEventListener("resize", () => ScrollTrigger.refresh());

      // keyboard path: focusing a world panel drives the pinned scrub to it
      const panels = [...track.children];
      track.addEventListener("focusin", (e) => {
        const panel = e.target.closest(".world-panel");
        if (!panel) return;
        const st = ScrollTrigger.getAll().find((s) => s.trigger === section);
        if (!st) return;
        const idx = panels.indexOf(panel);
        const target = st.start + (st.end - st.start) * (idx / (panels.length - 1));
        const viewport = section.querySelector(".worlds-viewport");
        if (viewport) viewport.scrollLeft = 0; // undo native focus-scroll desync
        window.scrollTo({ top: target, behavior: "auto" });
      });
    }

    const cm = document.querySelector(".craft-media video, .craft-media img");
    if (cm) gsap.fromTo(cm, { yPercent: -6 }, { yPercent: 6, ease: "none",
      scrollTrigger: { trigger: ".craft", start: "top bottom", end: "bottom top", scrub: true } });
  }

  if ($("hero-home")) {
    const loader = document.getElementById("site-loader");
    function _startHero() {
      // heroScrub runs the pinned scroll-scrub on desktop AND mobile. If anything throws
      // (old browser, GSAP hiccup), fall back to the plain looping video so scrolling is never broken.
      if (heroScrub) { try { heroScroll(); } catch (e) { console.error(e); heroStatic(); } }
      else heroStatic();
    }
    if (loader) {
      const minWait   = new Promise(r => setTimeout(r, 700));
      const fontsDone = typeof document.fonts !== "undefined" ? document.fonts.ready : Promise.resolve();
      const vidReady  = new Promise(r => { if (!video || video.readyState >= 1) r(); else { video.addEventListener("loadedmetadata", r, { once: true }); setTimeout(r, 2500); } });
      let revealed = false;
      function reveal() {
        if (revealed) return; revealed = true;
        document.body.classList.remove("loading");   // re-enable scroll before ST measures
        _startHero();                                 // create hero ST + pin spacer
        loader.classList.add("done");                 // fade out
        setTimeout(() => {
          loader.remove();
          if (heroScrub && typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
          startScrollAnimations();                    // worlds/craft ST after hero spacer exists
        }, 650);
      }
      Promise.all([minWait, fontsDone, vidReady]).then(reveal);
      setTimeout(reveal, 3500);   // safety net: never let the loader trap the page (iOS font/network stalls)
    } else {
      _startHero();
      startScrollAnimations();
    }
  } else {
    startScrollAnimations();
  }

  /* ====================================================
     SEASONAL tabs
     ==================================================== */
  const seasons = window.SEASON_DATA || {
    valentines: { h: "Valentine's Day - A glow worth sharing", p: "Deeper golds and Love in the Air: the romance this brand was built for. Limited mood edits and gift sets." },
    halloween: { h: "Halloween - Playful dark warmth", p: "Spiced amber, flickering ember worlds, and a little theater for the cozy-spooky season." },
    thanksgiving: { h: "Thanksgiving - Gather & give thanks", p: "Soulful Brew and Spice It Up for slow, grateful evenings around the table." },
    holiday: { h: "Holiday - A nook full of glow", p: "Curated gift sets, Love in the Air, and First Rain - wrapped, ready, and warm." },
  };
  const tabs = document.querySelectorAll(".season-tab");
  const sH = $("seasonH"), sP = $("seasonP");
  const seasonSection = document.querySelector(".seasonal");
  tabs.forEach((t) => t.addEventListener("click", () => {
    tabs.forEach((x) => { x.classList.remove("active"); x.setAttribute("aria-selected", "false"); });
    t.classList.add("active"); t.setAttribute("aria-selected", "true");
    if (seasonSection) seasonSection.dataset.season = t.dataset.season;
    const s = seasons[t.dataset.season];
    if (sH && sP) { sH.style.opacity = 0; sP.style.opacity = 0;
      setTimeout(() => { sH.textContent = s.h; sP.textContent = s.p; sH.style.opacity = 1; sP.style.opacity = 1; }, 200); }
  }));
})();
